// client/src/hooks/useLeagueActions.js
import { useCallback } from 'react'
import ApiLeagues from '../api/ApiLeagues'
import ApiRegistrations from '../api/ApiRegistrations'
import ApiDivisions from '../api/ApiDivisions'
import ApiMatches from '../api/ApiMatches'
import ApiPlayoffs from '../api/ApiPlayoffs'
import toastr from 'toastr'
import {
  formatStatus,
  getNextStatus,
  getNextStatusText,
} from '../utils/leagueStatusUtils'
import {
  getDivisionCreationRestrictedMessage,
  getDivisionAssignmentRestrictedMessage,
  LEAGUE_STATUS,
} from '../constants/leagueStatuses'
import {
  canCreateDivisions,
  canEditDivisions,
} from '../utils/divisionPermissions'
import {
  CROSS_LEVEL_PLACEMENT_NOT_ALLOWED,
  PLACEMENT_LEVEL_INACTIVE,
} from '../constants/leagueLevelErrors'
import { getActiveLevels } from '../utils/leagueLevels'

const mergeLeagueWithKnownLevels = (
  updatedLeague,
  currentLeague,
  existingLeague,
) => {
  const knownLeagueLevels = Array.isArray(updatedLeague?.league_levels)
    ? updatedLeague.league_levels
    : Array.isArray(currentLeague?.league_levels)
      ? currentLeague.league_levels
      : Array.isArray(existingLeague?.league_levels)
        ? existingLeague.league_levels
        : undefined

  if (!Array.isArray(knownLeagueLevels)) {
    return updatedLeague
  }

  return {
    ...updatedLeague,
    league_levels: knownLeagueLevels,
  }
}

/**
 * Custom hook to manage all action handlers for AdminLeagueView
 *
 * Centralizes all action handlers for league operations including:
 * - Status transitions and playoff management
 * - Division CRUD operations
 * - Player management
 * - Match operations
 * - Utility actions (CSV download, bracket updates)
 *
 * @param {Object} options - Configuration options
 * @param {string} options.selectedLeagueId - Currently selected league ID
 * @param {Object} options.selectedLeague - Currently selected league object
 * @param {Array} options.leagues - All leagues
 * @param {Function} options.setLeagues - Function to update leagues state
 * @param {Array} options.divisions - All divisions for selected league
 * @param {Object} options.assignments - Current division assignments
 * @param {Array} options.players - All players in selected league
 * @param {Array} options.unassignedPlayers - Players not assigned to any division
 * @param {Function} options.hasUnsavedChanges - Function to check for unsaved changes
 * @param {Function} options.loadLeagues - Function to reload all leagues
 * @param {Function} options.loadLeagueData - Function to reload league-specific data
 * @param {Function} options.setBracket - Function to update bracket state
 * @param {Function} options.setIsBracketEditMode - Function to toggle bracket edit mode
 * @param {Function} options.openDeleteModal - Function to open delete modal
 * @param {Function} options.closeDeleteModal - Function to close delete modal
 * @param {Function} options.openPlayoffModal - Function to open playoff modal
 * @param {Function} options.closePlayoffModal - Function to close playoff modal
 * @param {Function} options.openRemovePlayerModal - Function to open remove player modal
 * @param {Function} options.closeRemovePlayerModal - Function to close remove player modal
 * @param {Function} options.setLoadingState - Function to set loading states
 * @param {Function} options.resetNewDivision - Function to reset new division form
 * @param {Object} options.newDivision - New division form state
 * @param {Function} options.setSelectedLeagueId - Function to update selected league ID
 * @returns {Object} All action handlers
 */
export default function useLeagueActions({
  selectedLeagueId,
  selectedLeague,
  leagues,
  setLeagues,
  divisions,
  assignments,
  players,
  unassignedPlayers,
  hasUnsavedChanges,
  loadLeagues,
  loadLeagueData,
  setBracket,
  setIsBracketEditMode,
  openDeleteModal,
  closeDeleteModal,
  openPlayoffModal,
  closePlayoffModal,
  openRemovePlayerModal,
  closeRemovePlayerModal,
  setLoadingState,
  resetNewDivision,
  newDivision,
  setSelectedLeagueId,
}) {
  // ==================== League & Status Actions ====================

  const hydrateLeagueLevels = useCallback(async (league) => {
    if (
      Array.isArray(league?.league_levels) &&
      league.league_levels.length > 0
    ) {
      return league
    }

    const response = await ApiLeagues.getLeagueLevels(league.id)
    const leagueLevels = Array.isArray(response?.league_levels)
      ? response.league_levels
      : []

    return {
      ...league,
      league_levels: leagueLevels,
    }
  }, [])

  const handlePublishBracket = useCallback(
    async (league) => {
      setLoadingState('publishingBracket', true)
      try {
        // publishBracket is a league-wide status transition (playoffs_setup → playoffs).
        // No per-level loop is needed — calling it once publishes all levels.
        await ApiPlayoffs.publishBracket(league.id)
        // Refresh the specific league so status updates immediately in UI
        try {
          const updatedLeague = await ApiLeagues.getLeague(league.id)
          setLeagues((prev) => {
            const existingLeague = prev.find((l) => l.id === updatedLeague.id)
            const mergedLeague = mergeLeagueWithKnownLevels(
              updatedLeague,
              league,
              existingLeague,
            )

            return prev.map((l) =>
              l.id === mergedLeague.id ? mergedLeague : l,
            )
          })

          if (
            selectedLeagueId &&
            parseInt(selectedLeagueId) === updatedLeague.id
          ) {
            await loadLeagueData(selectedLeagueId)
          }
        } catch (refreshErr) {
          console.error(
            'Failed to refresh league after publishing bracket:',
            refreshErr,
          )
          await loadLeagues()
        }

        toastr.success(
          'Bracket published successfully! Users can now view the playoffs.',
        )
      } catch (err) {
        toastr.error(err.message || 'Failed to publish bracket')
        console.error('Publish bracket error:', err)
      } finally {
        setLoadingState('publishingBracket', false)
      }
    },
    [
      setLoadingState,
      setLeagues,
      selectedLeagueId,
      loadLeagueData,
      loadLeagues,
    ],
  )

  const handleLeagueDelete = useCallback(
    async (league) => {
      if (!league) return
      openDeleteModal(league)
    },
    [openDeleteModal],
  )

  const confirmLeagueDelete = useCallback(
    async (leagueToDelete) => {
      if (!leagueToDelete) return

      try {
        await ApiLeagues.remove(leagueToDelete.id)
        await loadLeagues()
        if (selectedLeagueId === leagueToDelete.id.toString()) {
          setSelectedLeagueId('')
        }
        closeDeleteModal()
        toastr.success('League deleted successfully!')
      } catch (err) {
        closeDeleteModal()
        // Display the specific error message from the backend
        const errorMessage =
          err.response?.data?.error || err.message || 'Unknown error'
        toastr.error('Failed to delete league: ' + errorMessage)
      }
    },
    [selectedLeagueId, loadLeagues, closeDeleteModal, setSelectedLeagueId],
  )

  const handleStatusTransition = useCallback(
    async (league) => {
      const nextStatus = getNextStatus(league.league_status)
      if (!nextStatus) return

      // Prevent moving from pending_start to active if there are unassigned players
      if (
        league.league_status === 'pending_start' &&
        nextStatus === 'active' &&
        unassignedPlayers.length > 0
      ) {
        toastr.error(
          'Cannot start league: There are unassigned players. Please assign all players to a division before starting the league.',
        )
        return
      }

      // Enforce minimum and maximum players per division when starting the league
      if (
        league.league_status === 'registration_closed' &&
        nextStatus === 'pending_start'
      ) {
        const min = Number(league.min_players_per_division)
        const max = Number(league.max_players_per_division)

        const underfilled = []
        const overfilled = []

        divisions.forEach((d) => {
          const assignedIds = assignments[d.id] || []
          const uniqueIds = Array.from(new Set(assignedIds))
          const count = uniqueIds.length
          const label = d.division_name || `Division ${d.id}`

          if (Number.isFinite(min) && min > 0 && count < min) {
            underfilled.push({ label, count })
          }
          if (Number.isFinite(max) && max > 0 && count > max) {
            overfilled.push({ label, count })
          }
        })

        if (underfilled.length > 0 || overfilled.length > 0) {
          const parts = []
          if (underfilled.length > 0) {
            parts.push(
              underfilled
                .map(
                  (u) =>
                    `${u.label} has only ${u.count} ${
                      u.count === 1 ? 'player' : 'players'
                    }, but the minimum is ${min}.`,
                )
                .join(' '),
            )
          }
          if (overfilled.length > 0) {
            parts.push(
              overfilled
                .map(
                  (o) =>
                    `${o.label} has ${o.count} ${
                      o.count === 1 ? 'player' : 'players'
                    }, but the maximum is ${max}.`,
                )
                .join(' '),
            )
          }

          toastr.error(`Cannot start league: ${parts.join(' ')}`)
          return
        }
      }

      // Special handling for starting playoffs - show detailed confirmation modal
      if (nextStatus === LEAGUE_STATUS.PLAYOFFS_SETUP) {
        // Fetch playoff eligibility information before showing modal
        try {
          const hydratedLeague = await hydrateLeagueLevels(league)
          const activeLevels = getActiveLevels(hydratedLeague)
          if (activeLevels.length > 0) {
            const perLevelEligibility = await Promise.all(
              activeLevels.map(async (level) => {
                const result = await ApiPlayoffs.getPlayoffEligibility(
                  league.id,
                  level.id,
                )
                return { level, eligibility: result.eligibility }
              }),
            )
            openPlayoffModal(hydratedLeague, perLevelEligibility)
          } else {
            const eligibility = await ApiPlayoffs.getPlayoffEligibility(
              hydratedLeague.id,
            )
            openPlayoffModal(hydratedLeague, eligibility.eligibility)
          }
        } catch (err) {
          console.error('Failed to load playoff eligibility:', err)
          toastr.error(
            'Failed to load playoff eligibility information. Please try again.',
          )
          return // Don't show modal if we can't get eligibility data
        }
        return
      }

      // Special handling for publishing bracket
      if (nextStatus === LEAGUE_STATUS.PLAYOFFS) {
        if (
          !window.confirm(
            'Are you sure you want to publish the bracket? Users will be able to see the playoffs after this.',
          )
        ) {
          return
        }
        await handlePublishBracket(league)
        return
      }

      // Add confirmation for other critical transitions
      const criticalTransitions = ['active', 'completed']
      if (criticalTransitions.includes(nextStatus)) {
        const actionText = getNextStatusText(league.league_status)
        if (
          !window.confirm(
            `Are you sure you want to ${actionText.toLowerCase()}? This action cannot be undone.`,
          )
        ) {
          return
        }
      }

      if (nextStatus === 'pending_start') {
        // For pending_start, use the existing publishLeague function
        try {
          await ApiLeagues.publishLeague(league.id)
          await loadLeagues()
          toastr.success('League divisions published successfully!')
        } catch (err) {
          toastr.error(err.message || 'Failed to publish league')
        }
        return
      }

      try {
        await ApiLeagues.updateState(league.id, nextStatus)
        await loadLeagues()
        toastr.success(`League status updated to ${formatStatus(nextStatus)}`)
      } catch (err) {
        toastr.error(err.message || 'Failed to update league status')
      }
    },
    [
      unassignedPlayers,
      divisions,
      assignments,
      hydrateLeagueLevels,
      openPlayoffModal,
      loadLeagues,
      handlePublishBracket,
    ],
  )

  const handleStartPlayoffs = useCallback(
    async (league) => {
      setLoadingState('startingPlayoffs', true)
      try {
        const hydratedLeague = await hydrateLeagueLevels(league)
        const activeLevels = getActiveLevels(hydratedLeague)
        if (activeLevels.length > 0) {
          for (const level of activeLevels) {
            try {
              await ApiPlayoffs.startPlayoffs(hydratedLeague.id, level.id)
            } catch (err) {
              throw new Error(
                `Failed to start playoffs for Level ${level.skill_level}: ${err.message}`,
              )
            }
          }
        } else {
          await ApiPlayoffs.startPlayoffs(hydratedLeague.id)
        }
        // Refresh the specific league so status updates immediately in UI
        try {
          const updatedLeague = await ApiLeagues.getLeague(hydratedLeague.id)
          setLeagues((prev) => {
            const found = prev.find((l) => l.id === updatedLeague.id)
            const mergedLeague = mergeLeagueWithKnownLevels(
              updatedLeague,
              hydratedLeague,
              found,
            )

            if (found)
              return prev.map((l) =>
                l.id === mergedLeague.id ? mergedLeague : l,
              )
            return [...prev, mergedLeague]
          })

          if (
            selectedLeagueId &&
            parseInt(selectedLeagueId) === updatedLeague.id
          ) {
            await loadLeagueData(selectedLeagueId)
          }
        } catch (refreshErr) {
          console.error(
            'Failed to refresh league after starting playoffs:',
            refreshErr,
          )
          await loadLeagues()
        }

        closePlayoffModal()
        toastr.success(
          'Playoffs started successfully! Bracket has been generated.',
        )
      } catch (err) {
        toastr.error(err.message || 'Failed to start playoffs')
        console.error('Playoff start error:', err)
      } finally {
        setLoadingState('startingPlayoffs', false)
      }
    },
    [
      setLoadingState,
      setLeagues,
      selectedLeagueId,
      loadLeagueData,
      loadLeagues,
      closePlayoffModal,
      hydrateLeagueLevels,
    ],
  )

  const handleSkipPlayoffs = useCallback(
    async (league) => {
      setLoadingState('startingPlayoffs', true)
      try {
        await ApiPlayoffs.skipPlayoffs(league.id)

        // Refresh the league data first, then show success
        await loadLeagues() // Reload entire list

        if (selectedLeagueId && parseInt(selectedLeagueId) === league.id) {
          await loadLeagueData(selectedLeagueId)
        }

        closePlayoffModal()
        toastr.success('Playoffs skipped. League marked as completed.')
      } catch (err) {
        toastr.error(err.message || 'Failed to skip playoffs')
        console.error('Skip playoffs error:', err)
      } finally {
        setLoadingState('startingPlayoffs', false)
      }
    },
    [
      setLoadingState,
      loadLeagues,
      selectedLeagueId,
      loadLeagueData,
      closePlayoffModal,
    ],
  )

  // ==================== Division Actions ====================

  const createDivision = useCallback(async () => {
    // Determine if league status allows creation before attempting API call
    const selectedLeague = leagues.find(
      (l) => l.id === parseInt(selectedLeagueId),
    )
    if (selectedLeague && !canCreateDivisions(selectedLeague)) {
      toastr.error(
        `Cannot create division: ${getDivisionCreationRestrictedMessage(
          selectedLeague.league_status,
        )}`,
      )
      return
    }

    // Check for unsaved player assignment changes
    if (hasUnsavedChanges()) {
      toastr.error(
        'Please save your division assignments before creating a new division to avoid losing your changes.',
      )
      return
    }

    if (
      !newDivision.division_name ||
      !newDivision.rank_level ||
      !newDivision.gender
    ) {
      toastr.error('Please fill in all required fields.')
      return
    }
    try {
      await ApiDivisions.create({
        ...newDivision,
        league_id: parseInt(selectedLeagueId),
      })
      toastr.success('Division created!')
      resetNewDivision()
      await loadLeagueData(selectedLeagueId)
    } catch (err) {
      // Surface specific backend error (e.g., league status restriction)
      const backendMessage = err?.response?.data?.error
      if (backendMessage) {
        toastr.error(backendMessage)
      } else {
        toastr.error(err.message || 'Error creating division')
      }
      console.error('Error creating division:', err)
    }
  }, [
    leagues,
    selectedLeagueId,
    hasUnsavedChanges,
    newDivision,
    resetNewDivision,
    loadLeagueData,
  ])

  const deleteDivision = useCallback(
    async (divisionId) => {
      if (!window.confirm('Are you sure you want to delete this division?'))
        return
      try {
        await ApiDivisions.remove(divisionId)
        toastr.success('Division deleted!')
        await loadLeagueData(selectedLeagueId)
      } catch (err) {
        toastr.error('Failed to delete division')
        console.error(err)
      }
    },
    [selectedLeagueId, loadLeagueData],
  )

  const updateDivisionName = useCallback(
    async (divisionId, newName) => {
      // Division names can be updated at any time - they're just labels
      // No status check needed for name-only updates
      try {
        await ApiDivisions.update({
          id: divisionId,
          division_name: newName,
        })
        toastr.success('Division name updated!')
        await loadLeagueData(selectedLeagueId)
      } catch (err) {
        const backendMessage = err?.response?.data?.error
        if (backendMessage) {
          toastr.error(backendMessage)
        } else {
          toastr.error('Failed to update division name')
        }
        console.error('Error updating division name:', err)
      }
    },
    [selectedLeagueId, loadLeagueData],
  )

  const saveAssignments = useCallback(async () => {
    if (selectedLeague && !canEditDivisions(selectedLeague)) {
      toastr.error(
        `Cannot save division assignments: ${getDivisionAssignmentRestrictedMessage(
          selectedLeague.league_status,
        )}`,
      )
      return
    }

    const changes = []
    try {
      const currentMap = {}
      for (const [divId, ids] of Object.entries(assignments)) {
        for (const playerId of ids) currentMap[playerId] = parseInt(divId)
      }
      const knownAssignments = {}
      for (const p of players) {
        if (p.division_id !== null) knownAssignments[p.id] = p.division_id
      }
      for (const id in knownAssignments) {
        if (!(id in currentMap))
          changes.push({ player_id: parseInt(id), division_id: null })
      }
      for (const [id, divId] of Object.entries(currentMap)) {
        if (knownAssignments[id] !== divId)
          changes.push({ player_id: parseInt(id), division_id: divId })
      }
      const payload = {
        league_id: parseInt(selectedLeagueId),
        changes,
      }
      await ApiDivisions.saveDraftAssignments(payload)
      toastr.success('Draft saved!')
      await loadLeagueData(selectedLeagueId)
    } catch (err) {
      const backendError = err?.response?.data?.error
      if (backendError === CROSS_LEVEL_PLACEMENT_NOT_ALLOWED) {
        const promptMessage = err?.response?.data?.message
          ? `${err.response.data.message}\n\nEnter override reason to continue, or Cancel to keep current assignments.`
          : 'Cross-level placement is not allowed.\n\nEnter override reason to continue, or Cancel to keep current assignments.'
        const overrideReason = window.prompt(promptMessage, '')
        if (overrideReason === null) return
        if (!overrideReason.trim()) {
          toastr.error(
            'Override reason is required to apply cross-level placement.',
          )
          return
        }

        const overridePlayerId = err?.response?.data?.player_id
        const overrideDivisionId = err?.response?.data?.division_id
        if (overridePlayerId == null || overrideDivisionId == null) {
          toastr.error(
            'Could not identify the conflicting player from the server error. Please retry.',
          )
          return
        }

        const retryChanges = changes.map((change) => {
          const isTargetedCrossLevelChange =
            Number(change.player_id) === overridePlayerId &&
            Number(change.division_id) === overrideDivisionId

          if (!isTargetedCrossLevelChange) return change
          return {
            ...change,
            override_level: true,
            override_reason: overrideReason.trim(),
          }
        })

        try {
          await ApiDivisions.saveDraftAssignments({
            league_id: parseInt(selectedLeagueId),
            changes: retryChanges,
          })
          toastr.warning('Cross-level placement saved with override.')
          await loadLeagueData(selectedLeagueId)
        } catch (retryErr) {
          const retryMessage =
            retryErr?.response?.data?.message ||
            retryErr?.response?.data?.error ||
            'Failed to save override.'
          toastr.error(retryMessage)
        }
        return
      }
      if (backendError === PLACEMENT_LEVEL_INACTIVE) {
        toastr.error(
          err?.response?.data?.message ||
            "This division's skill level is no longer active. Please reassign to an active level.",
        )
        return
      }

      const backendMessage =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message
      toastr.error(backendMessage || 'Failed to save draft')
      console.error(err)
    }
  }, [selectedLeague, assignments, players, selectedLeagueId, loadLeagueData])

  // ==================== Partner Actions ====================

  const handleDecouplePartner = useCallback(
    async (player) => {
      if (!player || !selectedLeagueId) return
      const partnerName = player.partner?.partner_name || 'their partner'
      if (
        !window.confirm(
          `Are you sure you want to decouple ${player.first_name} ${player.last_name} from ${partnerName}?`,
        )
      ) {
        return
      }

      try {
        await ApiRegistrations.decouplePartner({
          player_id: player.id,
          league_id: parseInt(selectedLeagueId, 10),
        })
        toastr.success(
          `${player.first_name} ${player.last_name} has been decoupled from ${partnerName}.`,
        )
        await loadLeagueData(selectedLeagueId)
      } catch (err) {
        const errorMessage =
          err.response?.data?.error || err.message || 'Unknown error'
        toastr.error('Failed to decouple partner: ' + errorMessage)
        console.error('Error decoupling partner:', err)
      }
    },
    [selectedLeagueId, loadLeagueData],
  )

  // ==================== Player Actions ====================

  const handleRemovePlayerClick = useCallback(
    (player) => {
      openRemovePlayerModal(player)
    },
    [openRemovePlayerModal],
  )

  const confirmRemovePlayer = useCallback(
    async (playerToRemove) => {
      if (!playerToRemove || !selectedLeagueId) return

      setLoadingState('removingPlayer', true)
      try {
        await ApiRegistrations.removePlayerFromLeague({
          player_id: playerToRemove.id,
          league_id: parseInt(selectedLeagueId),
        })
        toastr.success(
          `${playerToRemove.first_name} ${playerToRemove.last_name} removed from league successfully!`,
        )
        closeRemovePlayerModal()
        // Reload league data to refresh player list
        await loadLeagueData(selectedLeagueId)
      } catch (err) {
        const errorMessage =
          err.response?.data?.error || err.message || 'Unknown error'
        toastr.error('Failed to remove player: ' + errorMessage)
        console.error('Error removing player:', err)
      } finally {
        setLoadingState('removingPlayer', false)
      }
    },
    [selectedLeagueId, setLoadingState, closeRemovePlayerModal, loadLeagueData],
  )

  // ==================== Match Actions ====================

  const handleDeleteMatch = useCallback(
    async (matchId) => {
      if (
        !window.confirm(
          'Are you sure you want to delete this match? This action cannot be undone.',
        )
      ) {
        return
      }

      try {
        await ApiMatches.deleteMatch(matchId)
        toastr.success('Match deleted successfully')

        // Reload matches for current league
        if (selectedLeagueId) {
          await loadLeagueData(selectedLeagueId)
        }
      } catch (err) {
        toastr.error(err.message || 'Failed to delete match')
        console.error('Delete match error:', err)
      }
    },
    [selectedLeagueId, loadLeagueData],
  )

  const handleSwapHomeAway = useCallback(
    async (matchId) => {
      try {
        await ApiMatches.swapHomeAway(matchId)
        toastr.success('Home/away players swapped successfully')

        // Reload matches for current league
        if (selectedLeagueId) {
          await loadLeagueData(selectedLeagueId)
        }
      } catch (err) {
        toastr.error(err.message || 'Failed to swap home/away players')
        console.error('Swap home/away error:', err)
      }
    },
    [selectedLeagueId, loadLeagueData],
  )

  // ==================== Utility Actions ====================

  const handleDownloadParticipantsCsv = useCallback(
    async (leagueId) => {
      setLoadingState('downloadingCsv', true)
      try {
        const result = await ApiLeagues.downloadParticipantsCsv(leagueId)
        if (result.count !== null) {
          toastr.success(
            `Downloaded contact list for ${result.count} participant${result.count !== 1 ? 's' : ''}`,
          )
        } else {
          toastr.success('Participant list downloaded successfully!')
        }
      } catch (err) {
        toastr.error(err.message || 'Failed to download participant list')
        console.error('Download participants CSV error:', err)
      } finally {
        setLoadingState('downloadingCsv', false)
      }
    },
    [setLoadingState],
  )

  const handleBracketUpdate = useCallback(
    (updatedBracket) => {
      setBracket(updatedBracket)
      setIsBracketEditMode(false)
    },
    [setBracket, setIsBracketEditMode],
  )

  // Return all action handlers
  return {
    // Status & League
    handleStatusTransition,
    handleStartPlayoffs,
    handleSkipPlayoffs,
    handlePublishBracket,
    handleLeagueDelete,
    confirmLeagueDelete,

    // Division
    createDivision,
    deleteDivision,
    updateDivisionName,
    saveAssignments,

    // Player
    handleRemovePlayerClick,
    confirmRemovePlayer,
    handleDecouplePartner,

    // Match
    handleDeleteMatch,
    handleSwapHomeAway,

    // Utility
    handleDownloadParticipantsCsv,
    handleBracketUpdate,
  }
}
